import React, { useMemo } from "react";
import { DesignElement } from "./types";
import { getImageUrl } from "../../utils/imageUtils";
import { useApi } from "../../context/ApiContext";

interface DesignPreviewThumbnailProps {
  elements: DesignElement[];
  backgroundColor?: string;
  backgroundImage?: string;
  canvasSize: string;
  size?: number; // Size of the thumbnail in pixels (default: 80)
}

export const DesignPreviewThumbnail: React.FC<DesignPreviewThumbnailProps> = ({
  elements,
  backgroundColor = "#FFFFFF",
  backgroundImage,
  canvasSize,
  size = 80,
}) => {
  const { api } = useApi();

  // Canvas size presets (matching DesignCanvas)
  const canvasSizes: Record<string, { width: number; height: number }> = {
    mug: { width: 800, height: 600 },
    bottle: { width: 600, height: 800 },
    tshirt: { width: 1000, height: 1200 },
    poster: { width: 1200, height: 1800 },
  };

  const canvasDimensions = canvasSizes[canvasSize] || canvasSizes.mug;

  // Calculate scale to fit thumbnail size
  const scale = useMemo(() => {
    const scaleX = size / canvasDimensions.width;
    const scaleY = size / canvasDimensions.height;
    return Math.min(scaleX, scaleY);
  }, [size, canvasDimensions]);

  const scaledWidth = canvasDimensions.width * scale;
  const scaledHeight = canvasDimensions.height * scale;

  const renderElement = (element: DesignElement) => {
    const rotation = element.style.rotation || 0;
    const opacity = element.style.opacity ?? 1;
    const style: React.CSSProperties = {
      position: "absolute",
      left: `${element.position.x * scale}px`,
      top: `${element.position.y * scale}px`,
      width: `${element.size.width * scale}px`,
      height: `${element.size.height * scale}px`,
      zIndex: element.zIndex,
      transform: `rotate(${rotation}deg)`,
      transformOrigin: "center center",
      opacity: opacity,
    };

    switch (element.type) {
      case "text":
        return (
          <div
            key={element.id}
            style={{
              ...style,
              fontFamily:
                element.style.fontFamily ||
                "var(--store-font-family, 'Inter', sans-serif)",
              fontSize: `${(element.style.fontSize || 24) * scale}px`,
              fontWeight: element.style.fontWeight || "normal",
              fontStyle: element.style.fontStyle || "normal",
              textAlign:
                (element.style.textAlign as
                  | "left"
                  | "center"
                  | "right"
                  | "justify") || "left",
              color: element.style.color || "#000000",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              lineHeight: 1.2,
            }}
          >
            {element.content || "Text"}
          </div>
        );

      case "shape":
        return (
          <div
            key={element.id}
            style={{
              ...style,
              backgroundColor: element.content
                ? "transparent"
                : element.style.backgroundColor || "#000000",
              borderRadius: `${(element.style.borderRadius || 0) * scale}px`,
              border:
                element.style.borderWidth && element.style.borderColor
                  ? `${element.style.borderWidth * scale}px solid ${element.style.borderColor}`
                  : "none",
            }}
          >
            {element.content ? (
              <img
                src={getImageUrl(element.content, api.baseURL)}
                alt="Shape"
                className="w-full h-full object-contain"
                style={{
                  width: "100%",
                  height: "100%",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
                draggable={false}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : null}
          </div>
        );

      case "frame":
        return (
          <div
            key={element.id}
            style={{
              ...style,
              border: `${(element.style.borderWidth || 2) * scale}px solid ${element.style.borderColor || "#000000"}`,
              borderRadius: `${(element.style.borderRadius || 0) * scale}px`,
              backgroundColor: "transparent",
            }}
          />
        );

      default:
        return null;
    }
  };

  // Sort elements by zIndex
  const sortedElements = useMemo(() => {
    return [...elements].sort((a, b) => a.zIndex - b.zIndex);
  }, [elements]);

  return (
    <div
      className="relative overflow-hidden"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: backgroundColor,
      }}
    >
      <div
        className="absolute top-1/2 left-1/2"
        style={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          transform: "translate(-50%, -50%)",
        }}
      >
        {backgroundImage && (
          <img
            src={getImageUrl(backgroundImage, api.baseURL)}
            alt="Background"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 0 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        {sortedElements.map((element) => renderElement(element))}
      </div>
    </div>
  );
};
